import type { CharacterMeshFormat } from '../player/components/AnimatedCharacter';

/**
 * Infers mesh format from URL path (ignores query params / presigned signatures).
 */
export function inferMeshFormatFromPath(
  meshPath: string,
): CharacterMeshFormat | undefined {
  try {
    const pathOnly =
      meshPath.startsWith('http://') || meshPath.startsWith('https://')
        ? new URL(meshPath).pathname
        : meshPath.split('?')[0];
    const lower = pathOnly.toLowerCase();
    if (lower.endsWith('.fbx')) return 'fbx';
    if (lower.endsWith('.glb')) return 'glb';
    if (lower.endsWith('.gltf')) return 'gltf';
  } catch {
    /* ignore */
  }
  return undefined;
}

function inferMeshFormatFromExtension(
  extensionHint?: string | null,
): CharacterMeshFormat | undefined {
  const ext = extensionHint?.trim().toLowerCase().replace(/^\./, '');
  if (ext === 'fbx') return 'fbx';
  if (ext === 'glb') return 'glb';
  if (ext === 'gltf') return 'gltf';
  return undefined;
}

/**
 * Resolves GLB vs FBX for character mesh loading. Uses the path first, then an
 * optional extension hint when presigned URLs omit a file suffix.
 */
export function resolveCharacterMeshFormat(
  meshPath: string,
  extensionHint?: string | null,
  explicitFormat?: CharacterMeshFormat | null,
): CharacterMeshFormat | undefined {
  if (explicitFormat) return explicitFormat;
  return (
    inferMeshFormatFromPath(meshPath) ??
    inferMeshFormatFromExtension(extensionHint)
  );
}
